import { fadeIn, fadeOut } from '@hg/stylizator/animations'
import { LanguageSwitcher, ILanguageSwitcherLanguage, LinkButton } from '@hg/stylizator/components'
import { Text } from '@hg/stylizator/typography'
import { themeColors, Optional } from '@hg/stylizator/utils'
import classNames from 'classnames'
import * as React from 'react'
import styled from 'styled-components'

import ActiveMark from 'app/components/Header/ActiveMark'
import { IMenuItem, MOBILE_HEADER_MIN_WIDTH } from 'app/components/Header/model'

interface IProps {
  items: IMenuItem[]
  languages: ILanguageSwitcherLanguage[]
  opened?: boolean
}

const renderMenuItem = (item: IMenuItem, index?: number) => {
  if (item.classes?.includes('menu-item-primary-small')) {
    return (
      <li key={item.url + index}>
        <MenuButton
          className={classNames(item.classes)}
          type="primary"
          size="regular"
          href={item.url}
          target={item.target}
          fullWidth
        >
          {item.name}
        </MenuButton>
      </li>
    )
  }

  if (item.classes?.includes('menu-item-secondary-small')) {
    return (
      <li key={item.url + index}>
        <MenuButton
          className={classNames(item.classes)}
          type="secondary"
          size="regular"
          href={item.url}
          target={item.target}
          fullWidth
        >
          {item.name}
        </MenuButton>
      </li>
    )
  }

  return (
    <li key={item.url + index}>
      <Text type="heading_medium">
        <a href={item.url} target={item.target} rel="noopener" className={classNames(item.classes)}>
          <span className="menu-item-label">{item.name}</span>
          {item.active && <ActiveMark mobile />}
        </a>
      </Text>
    </li>
  )
}

interface Menus {
  left: IMenuItem[]
  right: IMenuItem[]
}

const MobileMenu: React.FC<IProps> = (props) => {
  const selectedLanguage = props.languages.find((lang) => lang.isSelected)

  const menus = props.items.reduce<Menus>(
    (menus, item) => {
      const isRightMenuItem = item.classes?.includes('right-menu-item')
      menus[isRightMenuItem ? 'right' : 'left'].push(item)
      return menus
    },
    { left: [], right: [] },
  )

  return (
    <WrapperView
      className={classNames({ fadeIn: props.opened === true, fadeOut: props.opened === false })}
      opened={props.opened}
    >
      <MenuListWrapper>
        {menus.left.map(renderMenuItem)}
        {selectedLanguage && (
          <LanguageSwitcher
            mode="inline"
            languages={props.languages}
            selectedLanguage={{
              abbreviation: selectedLanguage.abbreviation,
              label: selectedLanguage.label,
              isSelected: true,
            }}
          />
        )}
      </MenuListWrapper>
      <StickyMenu>{menus.right.map(renderMenuItem)}</StickyMenu>
    </WrapperView>
  )
}

const MenuListWrapper = styled.ul`
  list-style-type: none;
  margin: 60px 0 16px;
  padding: 0px;

  li {
    text-align: left;
    margin: 32px 0;

    a:hover .menu-item-label {
      color: ${themeColors.gray['60']};
    }

    a:active .menu-item-label {
      color: ${themeColors.gray['100']};
    }
  }
`

const WrapperView = styled.div<{ opened?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  visibility: hidden;
  position: fixed;
  width: 100%;
  background-color: white;
  top: 64px;
  z-index: 1001;
  box-shadow:
    0 4px 4px 0 rgba(35, 68, 101, 0.1),
    0 4px 4px 0 rgba(35, 68, 101, 0.1);
  height: calc(100% - 64px);
  overflow: hidden visible;
  padding: 0 24px;

  ${(props): Optional<string> => (props.opened !== true ? fadeOut : undefined)}
  ${(props): Optional<string> => (props.opened ? fadeIn : undefined)}

  @media (min-width: ${MOBILE_HEADER_MIN_WIDTH + 1}px) {
    display: none;
  }
`

const StickyMenu = styled.ul`
  list-style-type: none;
  position: sticky;
  bottom: 0;
  left: 0;
  padding: 24px 0;
  margin: 0;
  background-color: ${themeColors.white};

  li + li {
    margin-top: 16px;
  }
`

const MenuButton = styled(LinkButton)`
  width: 100%;
`

export default MobileMenu
